import { useMemo, useState } from "react";
import {
  conversationPreview,
  formatConversationTime,
} from "../domain/presentation";
import type { ConversationSummary } from "../domain/types";
import { Avatar } from "./Avatar";
import { Icon } from "./Icon";

export function ConversationList({
  conversations,
  selectedId,
  onSelect,
  onCreate,
}: {
  readonly conversations: readonly ConversationSummary[];
  readonly selectedId: string | null;
  readonly onSelect: (conversation: ConversationSummary) => void;
  readonly onCreate: () => void;
}) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const value = query.trim().toLocaleLowerCase();
    return value.length === 0
      ? conversations
      : conversations.filter((conversation) =>
          conversation.title.toLocaleLowerCase().includes(value) ||
          conversation.convId.toLocaleLowerCase().includes(value)
        );
  }, [conversations, query]);

  return (
    <aside className={`conversation-pane ${selectedId === null ? "mobile-visible" : ""}`}
      aria-label="会话列表">
      <div className="conversation-toolbar">
        <label className="search-field" aria-label="搜索会话">
          <Icon name="search" />
          <input value={query} onChange={(event) => { setQuery(event.target.value); }}
            onKeyDown={(event) => { if (event.key === "Escape") setQuery(""); }}
            placeholder="搜索" />
        </label>
        <button className="square-button" type="button" onClick={onCreate} aria-label="新建会话">
          <Icon name="plus" />
        </button>
      </div>
      <div className="conversation-scroll" role="list" aria-label={`${filtered.length} 个会话`}>
        {filtered.length === 0 ? (
          <div className="empty-list">
            <span><Icon name="chat" /></span>
            <strong>{query.trim() ? "未找到匹配会话" : "还没有会话"}</strong>
            <p>{query.trim() ? "换一个名称或会话 ID 试试" : "发起一次私聊或创建群聊吧"}</p>
            <button type="button" onClick={onCreate}>开始聊天</button>
          </div>
        ) : filtered.map((conversation) => {
          const preview = conversationPreview(conversation);
          const active = conversation.convId === selectedId;
          return (
            <div className="conversation-item" role="listitem" key={conversation.convId}>
              <button
                className={`conversation-row${active ? " active" : ""}${conversation.pinned ? " pinned" : ""}`}
                type="button"
                aria-current={active ? "page" : undefined}
                onClick={() => { onSelect(conversation); }}
              >
                <span className="conversation-avatar">
                  <Avatar name={conversation.title} url={conversation.avatarUrl} size={46} />
                  {conversation.unreadCount > 0 ? (
                    <b className={`unread-badge${conversation.muted ? " muted" : ""}`}
                      aria-label={`${conversation.unreadCount} 条未读消息`}>
                      {conversation.muted ? "" : conversation.unreadCount > 99 ? "99+" : conversation.unreadCount}
                    </b>
                  ) : null}
                </span>
                <span className="conversation-copy">
                  <span className="conversation-line">
                    <strong>{conversation.title}</strong>
                    <time dateTime={new Date(conversation.updatedAt).toISOString()}>
                      {formatConversationTime(conversation.updatedAt)}
                    </time>
                  </span>
                  <span className="conversation-line preview-line">
                    <span className={preview.accent === null ? "" : `preview-${preview.accent}`}>
                      {conversation.muted ? <span className="muted-prefix">免打扰 · </span> : null}
                      {preview.text}
                    </span>
                    {conversation.pinned ? <b className="pinned-label" aria-label="已置顶">⌃</b> : null}
                  </span>
                </span>
              </button>
            </div>
          );
        })}
      </div>
    </aside>
  );
}
